<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">
        {{ $t('inventory.operation.stock_transfer.stock_transfer') }}
      </h1>
      <ts-breadcrumb :items="[
        { text: $t('toHomePage'), href: '/' },
        {
          text: $t('inventory.operation.stock_transfer.stock_transfer'),
          href: '/stock-transfers'
        },
        { text: $t('inventory.operation.stock_transfer.edit'), active: true }
      ]" />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <form class="form-horizontal">
          <div class="row mb-3">
            <div class="col-sm-12 col-lg-6 col-md-12">
              <div class="form-group row">
                <label class="col-md-4 form-col-label control-label required">
                  {{ $t('inventory.operation.stock_transfer.date') }}
                </label>
                <div class="col-md-7">
                  <ts-date-picker-input v-model="model.transfer_date"></ts-date-picker-input>
                  <div class="validate" v-if="errors.has('transfer_date')">
                    {{ errors.first('transfer_date') }}
                  </div>
                </div>
              </div>
              <div class="form-group row">
                <label class="col-md-4 form-col-label control-label"> </label>
                <div class="col-md-7">
                  <ts-checkbox v-model="chk_current_branch" @change="setCurrentBranch">{{
                    $t('inventory.operation.stock_transfer.to_current_branch')
                    }}</ts-checkbox>
                </div>
              </div>
              <div class="form-group row" v-if="chk_current_branch">
                <label class="col-md-4 form-col-label control-label required">
                  {{ $t('inventory.operation.stock_transfer.to_location') }}
                  <i class="fas fa-spinner spin" v-if="!to_warehouses.length && ready_fetch"></i>
                </label>
                <div class="col-md-7">
                  <select class="form-control" v-model="model.to_location_id"
                    :disabled="!to_warehouses.length && ready_fetch"
                    :class="{ 'is-invalid': errors.has('to_location_id') }">
                    <option value="" disabled selected style="display: none">
                      {{ $t('select') }}
                    </option>
                    <option v-for="(warehouse, index) in to_warehouses" :key="index" :value="warehouse.location_id">
                      {{ warehouse.location_name }}
                    </option>
                  </select>
                  <div class="invalid-feedback" v-if="errors.has('to_location_id')">
                    {{ errors.first('to_location_id') }}
                  </div>
                </div>
              </div>
              <div class="form-group row" v-if="!chk_current_branch">
                <label class="col-md-4 form-col-label control-label required">
                  {{ $t('inventory.operation.stock_transfer.to_branch') }}
                </label>
                <div class="col-md-7">
                  <select class="form-control" v-model="model.to_branch_id"
                    :class="{ 'is-invalid': errors.has('to_branch_id') }">
                    <option value="" disabled selected style="display: none">
                      {{ $t('select') }}
                    </option>
                    <option v-for="(branch, index) in branches" :key="index" :value="branch.branch_id">
                      {{ branch.branch_name_en }}
                    </option>
                  </select>
                  <div class="invalid-feedback" v-if="errors.has('to_branch_id')">
                    {{ errors.first('to_branch_id') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
        <div class="row">
          <div class="col-md-12">
            <div id="container-table">
              <table class="table ui-table table-hover">
                <thead>
                  <tr>
                    <th style="width: 14%">
                      {{ $t('inventory.operation.stock_transfer.barcode') }}
                    </th>
                    <th style="width: 25%">
                      {{ $t('inventory.operation.stock_transfer.product') }}
                    </th>
                    <th style="width: 9%">
                      {{ $t('inventory.operation.stock_transfer.uom') }}
                    </th>
                    <!-- <th style="width: 7%">
                      {{ $t('inventory.operation.stock_transfer.cost') }}
                    </th> -->
                    <th class="text-center" style="width: 9%">
                      {{ $t('inventory.operation.stock_transfer.qty') }}
                    </th>
                    <th style="width: 10%">
                      {{ $t('inventory.operation.stock_transfer.amount') }}
                    </th>
                    <th style="width: 2%" class="text-center">
                      <a href="#" class="text-primary">
                        <i class="fas fa-spinner spin" v-if="ready_fetch"></i>
                        <i class="fa fa-plus" v-else @click.prevent="() => visible = !visible"></i>
                      </a>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in model.sub_item_list" :key="index">
                    <td>
                      <select class="form-control only-border-bottom" v-model="item.barcode"
                        @change="barcodeChange(item)">
                        <option value="" disabled selected style="display: none">
                          {{ $t('select') }}
                        </option>
                        <option v-for="(product, index) in products" :key="index" :value="product.barcode">
                          {{ product.barcode }}
                        </option>
                      </select>
                    </td>
                    <td>
                      <select class="form-control only-border-bottom" :class="{
                        'is-invalid': errors.has(
                          'sub_item_list.' + index + '.product_id'
                        )
                      }" v-model="item.product_id" @change="productChange(item)">
                        <option value="" disabled selected style="display: none">
                          {{ $t('select') }}
                        </option>
                        <option v-for="(product, index) in products" :key="index" :value="product.product_id">
                          {{ product.product_name_en }}
                        </option>
                      </select>
                      <div class="invalid-feedback" v-if="
                        errors.has('sub_item_list.' + index + '.product_id')
                      ">
                        {{
                          errors.first('sub_item_list.' + index + '.product_id')
                        }}
                      </div>
                    </td>
                    <td>
                      <select class="form-control only-border-bottom" :class="{
                        'is-invalid': errors.has(
                          'sub_item_list.' + index + '.uom_id'
                        )
                      }" v-model="item.uom_id" @change="onUomChange(item)">
                        <option value="" disabled selected style="display: none">
                          {{ $t('select') }}
                        </option>
                        <option v-for="(uom, index) in uomGetter(item)" :key="index" :value="uom.uom_id">
                          {{ uom.uom_name_en }}
                        </option>
                      </select>
                      <div class="invalid-feedback" v-if="errors.has('sub_item_list.' + index + '.uom_id')">
                        {{ errors.first('sub_item_list.' + index + '.uom_id') }}
                      </div>
                    </td>
                    <!-- <td>
                      <input disabled type="number" v-model.number="item.cost" step="any"
                        class="form-control only-border-bottom" />
                    </td> -->
                    <td>
                      <input type="number" v-model.number="item.qty" step="any" class="form-control only-border-bottom ts-text-center"
                        :class="{
                          'is-invalid': errors.has(
                            'sub_item_list.' + index + '.qty'
                          )
                        }" />
                      <div class="invalid-feedback" v-if="errors.has('sub_item_list.' + index + '.qty')">
                        {{ errors.first('sub_item_list.' + index + '.qty') }}
                      </div>
                    </td>
                    <td>
                      <input disabled type="number" step="any" class="form-control only-border-bottom"
                        :value="totalAmount(item)" />
                    </td>
                    <td class="text-center">
                      <a href="#" class="text-danger" @click.prevent="removeRow(item)">
                        <i class="fa fa-trash"></i>
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <table style="width: 100%" class="mt-2" id="amountValue" v-if="model.sub_item_list.length">
              <tr>
                <td style="width: 80%" class="text-right">
                  <label class="col-form-label">
                    {{ $t('inventory.operation.stock_transfer.grand_total') }} :
                  </label>
                </td>
                <td class="pl-2 pr-5" style="width: 16%">
                  <input disabled type="number" v-model.number="model.total_amount" step="any"
                    class="form-control only-border-bottom" />
                </td>
              </tr>
            </table>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-md-12 text-right">
            <ts-button class="btn-primary mr-2" @click.prevent="update" :waiting="saving"
              v-if="model.sub_item_list.length">
              {{ $t('update') }}
            </ts-button>
            <ts-button class="btn-default" @click="$router.push({ name: 'stock-transfer' })">
              {{ $t('cancel') }}
            </ts-button>
          </div>
        </div>
      </ts-card-wrapper>
    </ts-card>

    <a-modal v-model="visible" :title="$t('inventory.operation.stock_transfer.product')" :maskClosable="false"
      @ok="handleOk" :cancelText="$t('cancel')" :okText="$t('ok')" :zIndex="1020" width="400px">
      <div class="row">
        <div class="col-md-12">
          <input v-model="search" type="text" class="form-control background-search" :placeholder="$t('search')" />
        </div>
      </div>
      <br />

      <div style="max-height: 400px;" class="ts-overflow-y-auto">
        <div v-for="product in productList" :key="product.product_id">
          <ts-checkbox v-model="selectedProducts" class="font-12" :value="product">{{ product.product_name_en
            }}</ts-checkbox>
        </div>
      </div>
    </a-modal>

  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { Errors } from 'form-backend-validation'
import moment from 'moment'

export default {
  name: 'stock-transfer-edit',
  data() {
    return {
      selectedProducts: [],
      search: '',
      visible: false,
      model: {
        stock_transfer_id: 0,
        transfer_date: moment().format('DD-MM-YYYY'),
        to_location_id: '',
        to_branch_id: '',
        total_amount: null,
        sub_item_list: []
      },
      newRow: {
        stock_transfer_detail_id: 0,
        barcode: '',
        product_id: '',
        uom_id: '',
        cost: 0,
        qty: 1
      },
      chk_current_branch: false,
      errors: new Errors(),
      ready_fetch: false,
      saving: false,
      saving_new: false
    }
  },
  computed: {
    ...mapState('stockTransfer', [
      'to_warehouses',
      'products',
      'uom',
      'branches',
      'edit_data'
    ]),
    ...mapGetters('stockTransfer', ['uomGetter', 'productPrice', 'totalAmount']),
    productList() {
      const search = (this.search || '').toLowerCase()
      const selectedIds = new Set(
        this.model.sub_item_list.map(sb => sb.product_id)
      )

      return this.products.filter(el =>
        el.product_name_en.toLowerCase().includes(search) &&
        !selectedIds.has(el.product_id)
      )
    }
  },
  mounted() {
    this.fetchResourceBeforeCreate()

  },
  methods: {
    fetchResourceBeforeCreate() {
      this.ready_fetch = true
      this.$store
        .dispatch('stockTransfer/dataBeforeCreate')
        .then(async (response) => {
          this.setEditData()
        })
        .catch(error => {
          this.ready_fetch = false
          this.$toasted.error(error.message)
        })
        .finally(() => this.ready_fetch = false)
    },
    setCurrentBranch() {
      this.model.to_location_id = this.model.to_branch_id = ''
    },
    addNewRow() {
      this.model.sub_item_list.push(_.clone(this.newRow))
    },
    removeRow(item) {
      this.model.sub_item_list.splice(this.model.sub_item_list.indexOf(item), 1)
    },
    barcodeChange(item) {
      if (!item.barcode) {
        item.product_id = ''
        item.uom_id = ''
        return
      }
      let result = this.products.filter(p => p.barcode == item.barcode)
      item.product_id = result[0].product_id
      item.uom_id = result[0].sale_code[0].uom_id
      this.onUomChange(item)
    },
    productChange(item) {
      if (!item.product_id) {
        item.product_id = ''
        item.uom_id = ''
        return
      }
      let result = this.products.filter(p => p.product_id == item.product_id)
      item.barcode = result[0].barcode
      item.uom_id = result[0].sale_code[0].uom_id
      this.onUomChange(item)
    },
    onUomChange(item) {
      try {
        let productPrice = this.productPrice(item.product_id, item.uom_id)
        productPrice
          ? (item.cost = _.ceil(productPrice.cost, 2))
          : (item.cost = 0)
      } catch (e) {
        item.cost = ''
      }
    },
    grandTotal() {
      this.model.total_amount = _.ceil(
        _.sumBy(this.model.sub_item_list, detail => this.totalAmount(detail)),
        2
      )
    },

    update() {
      this.errors = new Errors()
      this.saving = true
      this.$store
        .dispatch(
          'stockTransfer/update',
          Object.assign({}, this.model, {
            chk_current_branch: this.chk_current_branch
          })
        )
        .then(response => {
          this.$toasted.success(response.message)
          this.$router.push({ name: 'stock-transfer' })
        })
        .catch(error => {
          this.saving = false
          if (
            error.message === 'To branch not found main warehouse.' ||
            error.message ===
            'Transfer across branch is for user that assign to main warehouse.' ||
            error.message ===
            'The current user not yet belong to any warehouses.'
          ) {
            this.$toasted.info(error.message)
          } else {
            this.$toasted.error(error.message)
          }
          this.errors = new Errors(error.errors)
        })
    },
    setEditData() {
      if (this.edit_data.branch_id === this.edit_data.to_branch_id) {
        this.chk_current_branch = true
      }
      this.model = {
        stock_transfer_id: this.edit_data.stock_transfer_id,
        transfer_date: this.edit_data.transfer_date,
        to_location_id: this.edit_data.to_location_id,
        to_branch_id: this.edit_data.to_branch_id,
        total_amount: this.edit_data.total_amount,
        sub_item_list: []
      }
      this.edit_data.stock_transfer_details.forEach(po => {
        this.model.sub_item_list.push(po)
      })
    },
    clearInput() {
      this.model = {
        transfer_date: moment().format('DD-MM-YYYY'),
        to_location_id: '',
        to_branch_id: '',
        sub_item_list: [],
        total_amount: null
      }
    },
    handleOk() {
      this.selectedProducts.forEach(element => {
        const saleCode = element.sale_code;
        const uomCat = element.u_o_m_category;
        let cost = 0;
        let uomDefaultId = undefined;

        if (uomCat) {
          const uomDefault = uomCat.unit_of_measures.find(u => u.is_default)
          uomDefaultId = uomDefault.uom_id;

          if (uomDefaultId) {
            const price = saleCode.find(code => code.uom_id == uomDefaultId).product_price
            if (price) {
              cost = parseFloat(price.cost)
            }
          }
        }


        this.model.sub_item_list.push({
          stock_transfer_detail_id: 0,
          barcode: element.barcode,
          product_id: element.product_id,
          uom_id: uomDefaultId,
          cost: cost,
          qty: 1
        })
      });

      this.selectedProducts.length = 0
    }
  },
  watch: {
    'model.sub_item_list': {
      handler() {
        this.grandTotal()
      },
      deep: true
    }
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('stockTransfer/RESET_STATE')
    this.$store.commit('stockTransfer/SET_TO_WAREHOUSES', [])
    this.$store.commit('stockTransfer/SET_BRANCHES', [])
    next()
  }
}
</script>
<style lang="css" scoped>
#container-table {
  max-height: 40vh;
  overflow-y: scroll;
  overflow-x: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

::-webkit-scrollbar {
  width: 3px;
  /* Remove scrollbar space */
  background: transparent;
  /* Optional: just make scrollbar invisible */
}

/* Optional: show position indicator in red */
::-webkit-scrollbar-thumb {
  background: rgb(194, 194, 194);
}

table td {
  vertical-align: middle;
}

.only-border-bottom {
  border-top: none;
  border-left: none;
  border-right: none;
  outline: none;
}

.table>thead>tr>th {
  position: sticky;
  position: -webkit-sticky;
  top: 0;
  z-index: 2;
}

.table>tbody>tr>td {
  padding-top: 5px !important;
  padding-bottom: 5px !important;
  border: none !important;
}

#amountValue tr>td {
  padding: 5px;
}
</style>
